/**
 * The mode table — what each `MultiplayerMode` in ./protocol actually means.
 *
 * ./protocol declares the SHAPE (`MatchModeSpec`) and deliberately no values:
 * the wire contract must not change when someone decides a co-op match should
 * be 3v3 instead of 2v2. This file is that decision, in one place, so that the
 * lobby UI, the matchmaker and the room all size a match identically. Three
 * independent copies of "duel means one per team" is exactly how a queue ends
 * up forming a room the server then refuses to start.
 *
 * DOM-safe and Node-safe: the lobby imports it in the browser and the server
 * imports it under Node, so nothing here may touch a socket, a clock or a
 * `document`.
 *
 * ===========================================================================
 * WHY THE QUEUE RULES ARE NOT IN `MatchModeSpec`
 * ===========================================================================
 *
 * `MatchModeSpec` is the part a CLIENT needs — how many per team, is the field
 * padded with bots, what to call it. How long a queue waits before it gives up
 * on finding a sixth human is a server policy, changes with how busy the box is,
 * and no client should be able to depend on it. So it lives beside the spec in
 * `ModeRules` rather than inside it, and only the server reads it.
 *
 * ===========================================================================
 * THE TABLE
 * ===========================================================================
 *
 *   duel      1v1  queued   fills with bots   ranked-style
 *   doubles   2v2  queued   fills with bots   ranked-style
 *   standard  3v3  queued   fills with bots   ranked-style
 *   private   3v3  code     no bot fill       whoever the host invites
 *   coop      3v3  queued   fills with bots   every human on blue, orange is bots
 *   freeplay  3v3  open     no bot fill       no teams, no clock, knockabout
 *
 * `coop` and `freeplay` are the two that are not symmetric, and both are
 * expressed as data rather than as a special case in the room:
 *
 *   - `humanTeam: 0` puts every human on blue however they joined, so the
 *     balancer never splits a co-op lobby across the halfway line.
 *   - `teams: false` tells the lobby not to draw a team column for freeplay.
 *     The SIMULATION still has two teams — a `CarState` has nowhere to be
 *     without one, and goals still count — it is the presentation and the
 *     balancer that stand down.
 */

import { DEFAULT_MATCH_CONFIG, type MatchConfig } from '../game/match';
import type { MatchModeSpec, MultiplayerMode } from './protocol';

/** Every mode, in menu order. */
export const MULTIPLAYER_MODES: readonly MultiplayerMode[] = Object.freeze([
  'duel',
  'doubles',
  'standard',
  'coop',
  'private',
  'freeplay',
]);

export const isMultiplayerMode = (value: unknown): value is MultiplayerMode =>
  typeof value === 'string' && (MULTIPLAYER_MODES as readonly string[]).includes(value);

/** Server-side policy for a mode, alongside the client-visible spec. */
export interface ModeRules {
  readonly spec: MatchModeSpec;
  /** Offered by the quick-match queue. `private` and `freeplay` are not. */
  readonly queued: boolean;
  /**
   * Humans a queue will start a match with once it has waited long enough. Below
   * this the queue keeps waiting; at or above it, bots make up the numbers. It is
   * never zero for a queued mode — a "match" of one human and five bots that the
   * player did not ask for is worse than a slightly longer wait.
   */
  readonly queueMin: number;
  /** Humans the queue holds out for while it still can. */
  readonly queueFull: number;
  /** Every human goes on this team; the rest of the field is bots. */
  readonly humanTeam: 0 | 1 | null;
  /** False when teams are cosmetic and the balancer should not run. */
  readonly teams: boolean;
  /** Match length in seconds, or null to use `DEFAULT_MATCH_CONFIG`. */
  readonly duration: number | null;
  /** Spectators allowed beyond the playing slots. */
  readonly maxSpectators: number;
}

const spec = (
  mode: MultiplayerMode,
  teamSize: number,
  fillWithBots: boolean,
  label: string
): MatchModeSpec => Object.freeze({ mode, teamSize, fillWithBots, label });

/**
 * Freeplay and private rooms run long — nobody wants a knockabout to stop after
 * five minutes — so both get an hour rather than the shipped 300 s. Not
 * "unlimited": a room whose clock never expires is a room that is never idle by
 * its own reckoning, and the reaper (see server/rooms.ts) should not be the only
 * thing that ever ends one.
 */
const LONG_MATCH_SECONDS = 3600;

export const MODE_RULES: Readonly<Record<MultiplayerMode, ModeRules>> = Object.freeze({
  duel: {
    spec: spec('duel', 1, true, 'Duel (1v1)'),
    queued: true,
    queueMin: 2,
    queueFull: 2,
    humanTeam: null,
    teams: true,
    duration: null,
    maxSpectators: 4,
  },
  doubles: {
    spec: spec('doubles', 2, true, 'Doubles (2v2)'),
    queued: true,
    queueMin: 2,
    queueFull: 4,
    humanTeam: null,
    teams: true,
    duration: null,
    maxSpectators: 4,
  },
  standard: {
    spec: spec('standard', 3, true, 'Standard (3v3)'),
    queued: true,
    queueMin: 2,
    queueFull: 6,
    humanTeam: null,
    teams: true,
    duration: null,
    maxSpectators: 6,
  },
  coop: {
    spec: spec('coop', 3, true, 'Co-op vs Bots'),
    queued: true,
    queueMin: 1,
    queueFull: 3,
    humanTeam: 0,
    teams: true,
    duration: null,
    maxSpectators: 6,
  },
  private: {
    spec: spec('private', 3, false, 'Private Match'),
    queued: false,
    queueMin: 0,
    queueFull: 0,
    humanTeam: null,
    teams: true,
    duration: LONG_MATCH_SECONDS,
    maxSpectators: 8,
  },
  freeplay: {
    spec: spec('freeplay', 3, false, 'Free Play'),
    queued: false,
    queueMin: 0,
    queueFull: 0,
    humanTeam: null,
    teams: false,
    duration: LONG_MATCH_SECONDS,
    maxSpectators: 8,
  },
});

export const modeRules = (mode: MultiplayerMode): ModeRules => MODE_RULES[mode];

export const modeSpec = (mode: MultiplayerMode): MatchModeSpec => MODE_RULES[mode].spec;

/** Playing slots in a room of this mode — cars, not connections. */
export const modeCapacity = (mode: MultiplayerMode): number => MODE_RULES[mode].spec.teamSize * 2;

/** Modes the quick-match queue offers, in menu order. */
export const QUEUED_MODES: readonly MultiplayerMode[] = Object.freeze(
  MULTIPLAYER_MODES.filter((mode) => MODE_RULES[mode].queued)
);

/**
 * The match rules a mode plays under. Only the duration ever differs, and it is
 * spread onto the shipped config rather than rebuilt, so a change to countdown
 * or replay length in ../game/match reaches every mode without being copied.
 */
export const matchConfigForMode = (mode: MultiplayerMode): MatchConfig => {
  const duration = MODE_RULES[mode].duration;
  return duration === null ? { ...DEFAULT_MATCH_CONFIG } : { ...DEFAULT_MATCH_CONFIG, duration };
};

/**
 * The team a joining player should take, given how many are already on each.
 *
 * Blue on a tie, which makes a one-human room deterministic rather than
 * dependent on join order. A mode that pins its humans to one team ignores the
 * counts entirely — that is what `humanTeam` means.
 */
export const balancedTeam = (
  mode: MultiplayerMode,
  onBlue: number,
  onOrange: number
): 0 | 1 => {
  const rules = MODE_RULES[mode];
  if (rules.humanTeam !== null) return rules.humanTeam;
  return onBlue <= onOrange ? 0 : 1;
};
